import '../dashboard/Dashboard.css';
import { useState,useEffect } from 'react';
import moment from 'moment';
import {useDashboardData} from './useDashboard';
import userProfileStore from "../core/UserProfileStore";
import useUserSettingStore from './UserSettingStore';
import { APP_ENV,  } from '../../constants';
import { commonAdminIdentityUrl, commonAdminLinkUrl } from './DashboardConfig';

export const CA_DASHBOARD = {
  DASHBOARD_ID: 'ca-dashboard',
  DOMAIN_ID: 'common-admin',
  WIDGET_ACC_DISABLED_ID: 'ca-disabled-user',
  WIDGET_MFA_ENABLED_ID: 'mfa-enabled-user',
  WIDGET_DMS_ASSGN_ID:'dms-assigned-user',
  WIDGET_ENTERPRISE_ROLE_ID:'enterprise-role-count',
  WIDGET_ENTERPRISE_STORE_ID:'enterprise-store-count',
  WIDGET_USERS_CREATED_ID: 'user-creation-filter'
};

const USER_CREATION_DATE_FORMAT = 'YYYY-MM-DD';
const USER_SEARCH_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZZ';

// Position of the users-created query in getDashboardConfig; keep in sync with
// the order the queries are pushed there.
export const USERS_CREATED_QUERY_INDEX = 2;

// Resolves the user-creation date range (and its display trend label) from the
// saved dashboard filter, which holds either a custom start/end date or a preset
// (6 = Last 7 Days, 13 = Last 14 Days, 30 = Last Month).
export const getUserCreationDateRange = (filter: any, t: any) => {
  let startDate;
  let endDate;
  let displayLabel = '';

  const customStart = filter?.startDate
    ? moment(filter.startDate, USER_CREATION_DATE_FORMAT)
    : null;
  const customEnd = filter?.endDate
    ? moment(filter.endDate, USER_CREATION_DATE_FORMAT)
    : null;
  const hasValidCustomRange = !!customStart && !!customEnd && customStart.isValid() && customEnd.isValid();

  if (hasValidCustomRange) {
    // Tolerate a reversed range (e.g. stale or hand-edited settings) by ordering it
    const [from, to] = customStart!.isAfter(customEnd!) ? [customEnd!, customStart!] : [customStart!, customEnd!];
    startDate = from;
    endDate = to;
    displayLabel = t('dashboard.customDateRange', {
      defaultValue: 'from {{startDate}} to {{endDate}}',
      startDate: startDate.format('MMMM D, YYYY'),
      endDate: endDate.format('MMMM D, YYYY'),
    });
  } else if (Number(filter?.dateRangePreset) > 0) {
    // dateRangePreset may come back from the backend as a string
    const daysAgo = Number(filter.dateRangePreset);
    startDate = moment().subtract(daysAgo, 'days').startOf('day');
    endDate = moment().endOf('day');
    if (daysAgo === 6) {
      displayLabel = t('dashboard.since1Week', 'since 1 week');
    } else if (daysAgo === 13) {
      displayLabel = t('dashboard.since2Weeks', 'since 2 weeks');
    } else if (daysAgo === 30) {
      displayLabel = t('dashboard.since1Month', 'since 1 month');
    } else {
      displayLabel = t('dashboard.customRange', 'custom range');
    }
  } else {
    startDate = moment().subtract(6, 'days').startOf('day');
    endDate = moment().endOf('day');
    displayLabel = t('dashboard.since1Week', 'since 1 week');
  }

  return { startDate, endDate, displayLabel };
};

// Single source of truth for the users-search request so the dashboard query
// and the filter-save path can't drift apart.
export const buildUsersCreatedApiConfig = (enterpriseId: string, filter: any, remoteUser: string = '') => {
  const { startDate, endDate } = getUserCreationDateRange(filter, (_key: string, fallback: any) =>
    typeof fallback === 'string' ? fallback : fallback?.defaultValue || ''
  );
  return {
    method: 'POST',
    path: '/1.1.1/rest/users/search',
    endpoint: commonAdminIdentityUrl[APP_ENV as unknown as keyof typeof commonAdminIdentityUrl],
    headers: {
      'REMOTE_USER': remoteUser || '',
    },
    body: {
      userSearchRequest: {
        enterpriseId,
        creationDateStart: startDate.clone().startOf('day').format(USER_SEARCH_DATE_TIME_FORMAT),
        creationDateEnd: endDate.clone().startOf('day').add(1, 'day').format(USER_SEARCH_DATE_TIME_FORMAT),
        offset: 0,
        limit: 499,
      },
    },
  };
};

// Returns today's date key and updates it when the day rolls over, so relative
// presets ("Last 7 days") are re-evaluated on dashboards left open overnight.
export const useCurrentDay = () => {
  const [day, setDay] = useState(() => moment().format(USER_CREATION_DATE_FORMAT));
  useEffect(() => {
    const check = () => {
      const today = moment().format(USER_CREATION_DATE_FORMAT);
      setDay((prev) => (prev === today ? prev : today));
    };
    const timer = setInterval(check, 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
  return day;
};

export const getUsersCreatedLink = (enterpriseId: string, filter: any, t: any) => {
  const { startDate, endDate } = getUserCreationDateRange(filter, t);
  const creationStartDate = startDate.format('MM-DD-YYYY');
  const creationEndDate = endDate.format('MM-DD-YYYY');
  return `${commonAdminLinkUrl[APP_ENV as keyof typeof commonAdminLinkUrl]}/${enterpriseId}/users?creationStartDate=${creationStartDate}&creationEndDate=${creationEndDate}`;
};

export const getWidgets = (t:any, enterpriseId: string, filter: any = {}) => {
  return [
      {
        id: CA_DASHBOARD.WIDGET_ACC_DISABLED_ID,
        dashboardId:CA_DASHBOARD.DASHBOARD_ID,
        loaderCount: 3,
        type: 'metric',
        size: 'small',
        title: t('dashboard.commonAdmin.deactivatedUser'),
        defaultView: 'metrics',
        availableViews: [ 'metrics' ],
        thresholdFields:[{ name:'Deactivated users'}],
        showPercentage:true,
        link: `${commonAdminLinkUrl[APP_ENV as keyof typeof commonAdminLinkUrl]}/${enterpriseId}/users?userStatus=disabled`,
        dataMapping:{
            transform: (sourceData: any) => {
                return {
                  name: 'Deactivated users',
                  total: sourceData[0]?.data?.totalUsers,
                  count: sourceData[0]?.data?.disabledUsersCount,
                  trend: `of ${sourceData[0]?.data?.totalUsers} users`
                };
              },
        }
      },
      {
        id: CA_DASHBOARD.WIDGET_DMS_ASSGN_ID,
        dashboardId:CA_DASHBOARD.DASHBOARD_ID,
        loaderCount: 3,
        type: 'metric',
        size: 'small',
        title: t('dashboard.commonAdmin.dmsProvisioned'),
        defaultView: 'metrics',
        availableViews: [ 'metrics' ],
        thresholdFields:[{ name:'DMS Provisioned users'}],
        showPercentage:true,
        link: `${commonAdminLinkUrl[APP_ENV as keyof typeof commonAdminLinkUrl]}/${enterpriseId}/users?cNumbers=${encodeURIComponent('[all]')}`,
        dataMapping:{
            transform: (sourceData: any) => {
                return {
                  name: 'DMS Provisioned users',
                  total: sourceData[0]?.data?.totalUsers,
                  count: sourceData[0]?.data?.dmsIdAssignedUsersCount,
                  trend: `of ${sourceData[0]?.data?.totalUsers} users`
                };
              },
        }
      },
      {
        id: CA_DASHBOARD.WIDGET_ENTERPRISE_ROLE_ID,
        dashboardId:CA_DASHBOARD.DASHBOARD_ID,
        loaderCount: 2,
        type: 'metric',
        size: 'small',
        title:t('dashboard.commonAdmin.noOfRoles'),
        defaultView: 'metrics',
        availableViews: [ 'metrics' ],
        thresholdFields:[{ name:'Number of roles'}],
        link: `${commonAdminLinkUrl[APP_ENV as keyof typeof commonAdminLinkUrl]}/${enterpriseId}/roles`,
        dataMapping:{
            transform: (sourceData: any) => {
                return {
                  name: 'Number of roles',
                  count: sourceData[1]?.data?.roleCount
                };
              },
        }
      },
      {
        id: CA_DASHBOARD.WIDGET_ENTERPRISE_STORE_ID,
        dashboardId:CA_DASHBOARD.DASHBOARD_ID,
        loaderCount: 2,
        type: 'metric',
        size: 'small',
        title: t('dashboard.commonAdmin.noOfStores'),
        defaultView: 'metrics',
        availableViews: [ 'metrics' ],
        thresholdFields:[{ name:'Number of stores'}],
        link: `${commonAdminLinkUrl[APP_ENV as keyof typeof commonAdminLinkUrl]}/${enterpriseId}/stores`,
        dataMapping:{
            transform: (sourceData: any) => {
                return {
                  name: 'Number of stores',
                  count: sourceData[1]?.data?.storeCount
                };
              },
        }
      },
      {
        id: CA_DASHBOARD.WIDGET_USERS_CREATED_ID,
        dashboardId:CA_DASHBOARD.DASHBOARD_ID,
        dataSourceId: 'ca-query-users-created',
        loaderCount: 2,
        type: 'metric',
        size: 'small',
        title: t('dashboard.commonAdmin.numberOfUsersCreated', 'Number of Users Created'),
        defaultView: 'metrics',
        availableViews: [ 'metrics' ],
        thresholdFields:[{ name:'Users Created'}],
        isFilterAvailable: true,
        link: getUsersCreatedLink(enterpriseId, filter, t),
        dataMapping:{
            transform: (sourceData: any) => {
                const { displayLabel } = getUserCreationDateRange(filter, t);
                return {
                  name: 'Users Created',
                  count: sourceData[USERS_CREATED_QUERY_INDEX]?.totalMatches || 0,
                  trend: displayLabel
                };
              },
        }
      },
      {
        id: CA_DASHBOARD.WIDGET_MFA_ENABLED_ID,
        dashboardId:CA_DASHBOARD.DASHBOARD_ID,
        loaderCount: 3,
        type: 'metric',
        size: 'small',
        title: t('dashboard.commonAdmin.mfaEnabled'),
        defaultView: 'metrics',
        availableViews: [ 'metrics' ],
        thresholdFields:[{ name:'MFA-compliant users'}],
        showPercentage:true,
        link: `${commonAdminLinkUrl[APP_ENV as keyof typeof commonAdminLinkUrl]}/${enterpriseId}/users?mfaEnabled=true`,
        dataMapping:{
            transform: (sourceData: any) => {
                return {
                  name: 'MFA-compliant users',
                  total: sourceData[0]?.data?.totalUsers,
                  count: sourceData[0]?.data?.mfaEnabledUsersCount,
                  trend: `of ${sourceData[0]?.data?.totalUsers} users`
                };
              },
        }
      }
    ];
}

export const getDashboardConfig = (enterpriseId:string, filter: any = {}, remoteUser: string = '') =>{
  const config = [];
  config.push(
          {
            id: 'ca-query',
            name: 'Overview',
            layout: 'default',
            apiConfig: {
              method:'GET',
              path:`/api/v1/users/metrics/${enterpriseId}?includeDisabled=true&includeMfaEnabled=true&includeDmsId=true`,
              endpoint: commonAdminIdentityUrl[APP_ENV as unknown as keyof typeof commonAdminIdentityUrl]
            },
          });
  config.push(
          {
            id: 'ca-query-role-store',
            name: 'Role',
            layout: 'default',
            apiConfig: {
              method:'GET',
              queryParams:{
                'enterpriseId': enterpriseId,
                'includeRole': 'true',
                'includeStore': 'true'
              },
              endpoint: '/api/common-admin/getRoleAndStoreCount'
            }
          });

  config.push(
          {
            id: 'ca-query-users-created',
            name: 'UsersCreated',
            layout: 'default',
            apiConfig: buildUsersCreatedApiConfig(enterpriseId, filter, remoteUser),
          });
  return config;
}


export const useCADashboard = ({ enabled = true, filteredWidgets = [] }: any = {}) => {

  const { userSetting } = useUserSettingStore();
  const {enterpriseId, iamUserProfile} = userProfileStore();
  const [config, setConfig] = useState() as any;
  const {isLoading, error, getWidgetData, getWidgetError } = useDashboardData({config,enabled: enabled && !!config});
  const [widgets , setWidgets] = useState(filteredWidgets) as any;
  const today = useCurrentDay();

  const dashboardFilter = userSetting?.dashboard?.find((d: any) => d.id === CA_DASHBOARD.DASHBOARD_ID)?.filter || {};

  useEffect(()=>{
    if (!enabled) {
      setConfig(null);
      return;
    }
    if(enterpriseId){
      setConfig(getDashboardConfig(enterpriseId, dashboardFilter, iamUserProfile?.username));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[
    enterpriseId,
    enabled,
    iamUserProfile?.username,
    dashboardFilter.startDate,
    dashboardFilter.endDate,
    dashboardFilter.dateRangePreset,
    today,
  ])
    
  
    useEffect(() => {
  
      if (userSetting && iamUserProfile && enterpriseId) {
        let isExternallyFederated = iamUserProfile?.fullUser?.user?.externallyFederated;
        const dashboard = userSetting.dashboard?.find((dash: any) => dash.id === CA_DASHBOARD.DASHBOARD_ID);
        const savedWidgets = dashboard?.widgets || [];
        const updatedWidgets = 
          filteredWidgets
          .filter((widget: any) => {
              if (isExternallyFederated && widget.id === 'mfa-enabled-user') {
                return false;
              }
              return true;
          })
          .map((dwidget: any) => {
            const savedWidget = savedWidgets.find((w: any) => w.id === dwidget.id);
            return {
              ...dwidget,
              fields: savedWidget?.fields,
              view: savedWidget?.view,
            };
          });
        setWidgets(updatedWidgets);
  
      }
    }, [userSetting, enterpriseId, iamUserProfile, filteredWidgets]);
    
  return {
    dashboardId:CA_DASHBOARD.DASHBOARD_ID,
    widgets,
    isLoading: enabled ? (isLoading || !config) : false,
    error,
    filter: dashboardFilter,
    getWidgetData,
    getWidgetError,
  };
};